import React from 'react'
import { useContent } from '../../contexts/ContentContext'
import SectionWrapper from '../ui/SectionWrapper'
import SectionLabel from '../ui/SectionLabel'
import TopicCard from '../ui/TopicCard'
import CtaButton from '../ui/CtaButton'

/**
 * ForWhomSection — regression TopicCard grid template.
 *
 * contentKey → block:
 * {
 *   id?, label?, headline?, intro?, subline?,
 *   columns?: 2 | 3 | 4,   // md+ cols; 3 + 6 topics = 2 rows
 *   topics?: { title, body? }[],
 *   // list strings → title-only TopicCards (same craft, never plain list cells)
 *   items?: string[],
 *   closingQuote?, cta?
 * }
 */
export default function ForWhomSection({ contentKey = 'forWhom' }) {
  const content = useContent()
  const data = content[contentKey] || content.forWhom
  if (!data) return null

  const topics =
    data.topics?.length
      ? data.topics
      : (data.items || []).map((item) =>
          typeof item === 'string' ? { title: item } : item
        )

  if (!topics.length && !data.headline) return null

  // Default: regression-style 4-col; content can force 3 (e.g. outcomes 3×2)
  const columns = data.columns ?? 4
  const colClass =
    columns === 2
      ? 'md:grid-cols-2'
      : columns === 3
        ? 'md:grid-cols-3'
        : 'md:grid-cols-4'

  return (
    <SectionWrapper
      data-section="ForWhomSection"
      id={data.id || (contentKey === 'forWhom' ? 'fuer-wen' : contentKey)}
      data-testid={`forwhom-${contentKey}`}
    >
      <div className="max-w-centered-header text-center mx-auto section-padding-sm">
        {data.label ? <SectionLabel text={data.label} /> : null}
        {data.headline ? (
          <h2 className="typography-h2-mobile md:typography-h2 leading-tight heading-body-spacing">
            {data.headline}
          </h2>
        ) : null}
        {data.intro ? (
          <p className="typography-body-lg leading-relaxed">{data.intro}</p>
        ) : null}
        {data.subline ? (
          <p className="typography-body-lg text-color-primary font-medium mt-3">
            {data.subline}
          </p>
        ) : null}
      </div>

      {topics.length ? (
        <div
          className={`margin-top-md grid grid-cols-1 sm:grid-cols-2 ${colClass} card-grid-gap-sm md:card-grid-gap-md`}
          data-testid="topic-cards-grid"
          data-columns={columns}
        >
          {topics.map((topic, i) => (
            <TopicCard key={i} title={topic.title} body={topic.body} />
          ))}
        </div>
      ) : null}

      {data.closingQuote ? (
        <blockquote className="mt-12 max-w-2xl mx-auto text-center">
          <p className="typography-h3-mobile md:typography-h3 text-color-primary italic leading-snug">
            {data.closingQuote}
          </p>
        </blockquote>
      ) : null}

      {data.cta ? (
        <div className="mt-10 text-center">
          <CtaButton label={data.cta} variant="primary" className="py-4 px-10" />
        </div>
      ) : null}
    </SectionWrapper>
  )
}
